import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Input as AntInput, Button as AntButton, Dropdown, Checkbox, Space, Modal as AntModal, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { getReservationTravelerList, getReservationTravelerListBySaleNo, getOrderStatusList, saveSaleMemo, createMaritReservationBatch, createMaritReservationForRegisteredProducts, getSaleStatusCodes, updateReservationBatch, getGroupSettlementMain } from '../services/apiService';
import NewReservationModal from '../components/NewReservationModal';
import MaritReservationImportModal from '../components/MaritReservationImportModal';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import DateRangePicker from '../components/DateRangePicker';
import { getUserInfo } from '../utils/tokenManager';

// Columns required for reservation management traveler list
const columns = [
  { key: '예약정보', label: '예약정보', type: 'string' },
  { key: '일정/상품', label: '일정/상품', type: 'string' },
  { key: '예약일', label: '예약일', type: 'date' },
  { key: '행사시작일', label: '출발일', type: 'date' },
  { key: '판매상품명', label: '상품명', type: 'string' },
  { key: '수배담당자', label: '수배 담당', type: 'string' },
  { key: '도시', label: '도시', type: 'string' },
  { key: '연령구분', label: '연령구분', type: 'string' },
  { key: '한글이름', label: '한글이름', type: 'string' },
  { key: '성별', label: '성별', type: 'string' },
  { key: '영문성', label: '영문성', type: 'string' },
  { key: '영문이름', label: '영문이름', type: 'string' },
  { key: '생년월일', label: '생년월일', type: 'date' },
  { key: '여권번호', label: '여권번호', type: 'string' },
  { key: '여권만료일', label: '여권만료일', type: 'date' },
  { key: '여행객비고', label: '여행객비고', type: 'string' },
  { key: '상품금액', label: '상품금액', type: 'number' },
  { key: '추가판매', label: '추가판매', type: 'number' },
  { key: '입금상태', label: '입금상태', type: 'string' },
  { key: '예약상태', label: '예약상태', type: 'string' },
  { key: '주문정보', label: '주문/대리점', type: 'string' },
  { key: '주문번호', label: '주문번호', type: 'string' },
  { key: '대리점', label: '대리점', type: 'string' },
];

const MERGED_ORDER_COLUMN_KEYS = new Set([
  '예약일',
  '행사시작일',
  '판매상품명',
  '수배담당자',
  '도시',
  '입금상태',
  '예약상태',
  '주문정보',
  '주문번호',
  '대리점',
]);
// 합쳐진 컬럼(주문정보)은 기본 숨김 — 주문번호/대리점은 분리 표시
const FORCE_HIDDEN_COLUMN_KEYS = new Set([]);
// Composite columns embed these fields; toggling one side should hide the other to avoid "숨겨도 그대로 보임"
const COMPOSITE_TO_PARTS = {
  '예약정보': ['입금상태', '예약상태', '주문번호', '대리점'],
  '일정/상품': ['예약일', '행사시작일', '판매상품명'],
  '주문정보': ['주문번호', '대리점'],
};
const TOGGLEABLE_COLUMNS = columns.filter((c) => !FORCE_HIDDEN_COLUMN_KEYS.has(c.key));

function migrateVisibleColsSplitOrderAgency(restored, validKeys) {
  if (!restored) return restored;
  const hasCombined = restored.has('주문정보');
  const hasOrderNo = restored.has('주문번호');
  const hasAgency = restored.has('대리점');

  if (hasCombined) {
    restored.delete('주문정보');
    if (!hasOrderNo && validKeys.has('주문번호')) restored.add('주문번호');
    if (!hasAgency && validKeys.has('대리점')) restored.add('대리점');
    return restored;
  }

  // 예전에는 주문번호/대리점이 강제 숨김이라 저장되지 않음 → 둘 다 없으면 기본으로 분리 표시
  if (!hasOrderNo && !hasAgency) {
    if (validKeys.has('주문번호')) restored.add('주문번호');
    if (validKeys.has('대리점')) restored.add('대리점');
  }
  return restored;
}

function applyVisibleColToggle(prev, key, checked) {
  const next = new Set(prev);
  FORCE_HIDDEN_COLUMN_KEYS.forEach((k) => next.delete(k));
  if (checked) {
    next.add(key);
    const parts = COMPOSITE_TO_PARTS[key];
    if (parts) {
      parts.forEach((p) => next.delete(p));
    } else {
      Object.entries(COMPOSITE_TO_PARTS).forEach(([composite, parts]) => {
        if (parts.includes(key)) next.delete(composite);
      });
    }
  } else {
    next.delete(key);
  }
  return next;
}

function formatNumberWithCommas(value) {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString('ko-KR');
}

function formatCellValue(key, value) {
  if (key === '상품금액' || key === '추가판매') {
    return formatNumberWithCommas(value);
  }
  // Generic date normalization for cells declared as 'date'
  if (columns.find(c => c.key === key)?.type === 'date') {
    if (value === null || value === undefined || value === '') return '';
    const s = String(value).trim();
    // Handle compact numeric dates like 19700721 -> 1970-07-21
    if (/^\d{8}$/.test(s)) {
      const y = s.slice(0, 4);
      const m = s.slice(4, 6);
      const d = s.slice(6, 8);
      return `${y}-${m}-${d}`;
    }
    if (s.includes(' ')) return s.split(' ')[0];
    if (s.includes('T')) return s.split('T')[0];
    const m = s.match(/^\d{4}[./-]\d{1,2}[./-]\d{1,2}/);
    if (m) return m[0];
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return s;
  }
  return value ?? '';
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(amount || 0);
}

function formatDateOnly(v) {
  const s = String(v || '').trim();
  if (!s) return '';
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return s;
}

 function getMouseAnchorRect(e) {
   const x = typeof e?.clientX === 'number' ? e.clientX : (typeof e?.nativeEvent?.clientX === 'number' ? e.nativeEvent.clientX : null);
   const y = typeof e?.clientY === 'number' ? e.clientY : (typeof e?.nativeEvent?.clientY === 'number' ? e.nativeEvent.clientY : null);
   if (x === null || y === null) return null;
   return { left: x, right: x, top: y, bottom: y, width: 0, height: 0 };
 }

const ReservationManagement = ({ embedded = false, hideTabs = false, fixedMode = null } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyNo } = getUserInfo();
  const canUseMaritImport = String(companyNo || '') === '1';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  const [activeMode, setActiveMode] = useState(fixedMode || 'reservations');
  const isSettlementReservationEmbedded = embedded && fixedMode === 'reservations';

  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [groupDrawerSaleNo, setGroupDrawerSaleNo] = useState(null);
  const [groupDrawerTitle, setGroupDrawerTitle] = useState('');

  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderSettleRows, setOrderSettleRows] = useState([]);
  const [orderSearchBasis, setOrderSearchBasis] = useState('행사일');
  const [orderStartDate, setOrderStartDate] = useState('');
  const [orderEndDate, setOrderEndDate] = useState('');
  const [orderGroupNoFilter, setOrderGroupNoFilter] = useState('');
  const [orderGroupNameFilter, setOrderGroupNameFilter] = useState('');
  const [orderKeyword, setOrderKeyword] = useState('');
  const [orderReservationStatusFilter, setOrderReservationStatusFilter] = useState('all');

  const [saleMemoModalOpen, setSaleMemoModalOpen] = useState(false);
  const [saleMemoModalValue, setSaleMemoModalValue] = useState('');
  const [saleMemoModalSaleNo, setSaleMemoModalSaleNo] = useState(null);
  const [saleMemoModalOrderNo, setSaleMemoModalOrderNo] = useState('');
  const [saleMemoSaving, setSaleMemoSaving] = useState(false);

  // 검색기준(1=예약일, 2=행사일) 및 날짜 범위 상태
  const [searchBasis, setSearchBasis] = useState('2');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // optional filters/sort
  const [colFilters, setColFilters] = useState({});
  const [globalFilter] = useState('');
  const [density, setDensity] = useState('comfortable');
  const VISIBLE_COLS_STORAGE_KEY = isSettlementReservationEmbedded
    ? 'settlements_reservation_visible_cols_v2'
    : 'reservation_visible_cols_v1';
  const [visibleCols, setVisibleCols] = useState(() => {
    const next = new Set(columns.map(c => c.key));
    const isSettlementReservationEmbeddedInitial = isSettlementReservationEmbedded;

    // 1) 저장된 표시 열 복원 (정산 임베디드=sessionStorage, 일반=localStorage)
    if (typeof window !== 'undefined' && VISIBLE_COLS_STORAGE_KEY) {
      try {
        const storage = isSettlementReservationEmbeddedInitial ? window.sessionStorage : window.localStorage;
        const raw = storage.getItem(VISIBLE_COLS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (Array.isArray(parsed) && parsed.length > 0) {
          const validKeys = new Set(TOGGLEABLE_COLUMNS.map(c => c.key));
          const restored = new Set();
          parsed.forEach((k) => {
            if (validKeys.has(k)) restored.add(k);
          });
          if (restored.size > 0) {
            if (isSettlementReservationEmbeddedInitial) {
              const migrationFlagKey = `${VISIBLE_COLS_STORAGE_KEY}__migrated_city_manager_v1`;
              const alreadyMigrated = window.sessionStorage.getItem(migrationFlagKey) === '1';
              if (!alreadyMigrated) {
                ['수배담당자', '도시'].forEach((k) => {
                  if (validKeys.has(k)) restored.add(k);
                });
                try {
                  window.sessionStorage.setItem(migrationFlagKey, '1');
                  window.sessionStorage.setItem(VISIBLE_COLS_STORAGE_KEY, JSON.stringify(Array.from(restored)));
                } catch (_) {}
              }
            }
            // 주문정보(합침) → 주문번호/대리점 분리 마이그레이션
            const splitOrderFlagKey = `${VISIBLE_COLS_STORAGE_KEY}__migrated_split_order_agency_v1`;
            const alreadySplitMigrated = (() => {
              try {
                return storage.getItem(splitOrderFlagKey) === '1';
              } catch {
                return false;
              }
            })();
            if (!alreadySplitMigrated) {
              migrateVisibleColsSplitOrderAgency(restored, validKeys);
              try {
                storage.setItem(splitOrderFlagKey, '1');
                storage.setItem(VISIBLE_COLS_STORAGE_KEY, JSON.stringify(Array.from(restored)));
              } catch (_) {}
            } else if (restored.has('주문정보') && !restored.has('주문번호') && !restored.has('대리점')) {
              // 플래그는 있으나 합침 컬럼만 남은 경우 보정
              migrateVisibleColsSplitOrderAgency(restored, validKeys);
            }
            FORCE_HIDDEN_COLUMN_KEYS.forEach((k) => restored.delete(k));
            return restored;
          }
        }
      } catch (_) {}
    }

    // 2) 저장된 값이 없으면 기존 기본값 로직 유지
    if (isSettlementReservationEmbeddedInitial) {
      // Composite columns are now shown in a group header row, so keep them hidden by default
      ['예약정보', '일정/상품', '주문정보'].forEach((k) => next.delete(k));
    } else {
      // Default: keep original columns visible, hide composite columns
      ['예약정보', '일정/상품', '주문정보'].forEach((k) => next.delete(k));
      // Only show this derived column in /settlements embedded view by default
      next.delete('추가판매');
    }
    FORCE_HIDDEN_COLUMN_KEYS.forEach((k) => next.delete(k));
    return next;
  });
  const [showColMenu, setShowColMenu] = useState(false);
  const containerRef = useRef(null);
  const travelerHoverTimerRef = useRef(null);
  const travelerCloseTimerRef = useRef(null);
  const travelerRequestIdRef = useRef(0);
  const travelerCacheRef = useRef(new Map());
  const travelerPendingRef = useRef({ saleNo: null, anchorRect: null });
  const travelerPopoverRootRef = useRef(null);
  const [travelerPopover, setTravelerPopover] = useState({ open: false, saleNo: null, anchorRect: null, loading: false, error: null, list: [] });
  // 드래그 가능한 컬럼 순서 상태 (localStorage 유지)
  const COL_ORDER_STORAGE_KEY = isSettlementReservationEmbedded
    ? 'settlements_reservation_col_order_v2'
    : 'reservation_col_order_v1';
  const defaultColOrder = useMemo(() => columns.map(c => c.key), []);
  const [colOrder, setColOrder] = useState(() => {
    try {
      const storage = (typeof window !== 'undefined' && isSettlementReservationEmbedded)
        ? window.sessionStorage
        : window.localStorage;
      const raw = storage.getItem(COL_ORDER_STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(saved) || saved.length === 0) return defaultColOrder;
      // 저장된 키와 현재 키를 병합 (누락/신규 대응)
      const setSaved = new Set(saved);
      const merged = saved.filter(k => defaultColOrder.includes(k));
      defaultColOrder.forEach(k => {
        if (setSaved.has(k)) return;
        if (k === '추가판매') {
          const idx = merged.indexOf('상품금액');
          if (idx !== -1) merged.splice(idx + 1, 0, k);
          else merged.push(k);
          return;
        }
        merged.push(k);
      });
      return merged;
    } catch {
      return defaultColOrder;
    }
  });
  const [draggingColKey, setDraggingColKey] = useState(null);
  // 취소 예약 표시 여부 (기본: 숨김)
  const [showCancelled, setShowCancelled] = useState(false);
  // 신규예약 모달
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [maritImportModalOpen, setMaritImportModalOpen] = useState(false);
  const closeTravelerPopover = () => {
    setTravelerPopover((prev) => ({ ...prev, open: false }));
  };

  const openTravelerPopover = async ({ saleNo, anchorRect }) => {
    if (!saleNo || !anchorRect) return;
    if (travelerCloseTimerRef.current) {
      clearTimeout(travelerCloseTimerRef.current);
      travelerCloseTimerRef.current = null;
    }

    const cached = travelerCacheRef.current.get(String(saleNo));
    if (Array.isArray(cached)) {
      setTravelerPopover({ open: true, saleNo, anchorRect, loading: false, error: null, list: cached });
      return;
    }

    const requestId = ++travelerRequestIdRef.current;
    setTravelerPopover({ open: true, saleNo, anchorRect, loading: true, error: null, list: [] });
    try {
      const list = await getReservationTravelerListBySaleNo({ saleNo });
      if (requestId !== travelerRequestIdRef.current) return;
      if (!travelerPendingRef.current || String(travelerPendingRef.current.saleNo) !== String(saleNo)) return;
      const normalized = Array.isArray(list) ? list : [];
      travelerCacheRef.current.set(String(saleNo), normalized);
      setTravelerPopover({ open: true, saleNo, anchorRect, loading: false, error: null, list: normalized });
    } catch (e) {
      if (requestId !== travelerRequestIdRef.current) return;
      setTravelerPopover({ open: true, saleNo, anchorRect, loading: false, error: '여행객 정보를 불러오지 못했습니다.', list: [] });
    }
  };

  const handleOrderRowMouseEnter = (e, row) => {
    const s = row?.대표판매번호 ?? row?.판매번호 ?? null;
    if (!s) return;
    if (travelerCloseTimerRef.current) {
      clearTimeout(travelerCloseTimerRef.current);
      travelerCloseTimerRef.current = null;
    }
    if (travelerHoverTimerRef.current) clearTimeout(travelerHoverTimerRef.current);
    travelerPendingRef.current = { saleNo: s, anchorRect: getMouseAnchorRect(e) };
    travelerHoverTimerRef.current = setTimeout(() => {
      openTravelerPopover(travelerPendingRef.current);
    }, 500);
  };

  const handleOrderRowMouseMove = (e, row) => {
    if (!travelerHoverTimerRef.current) return;
    const s = row?.대표판매번호 ?? row?.판매번호 ?? null;
    if (!s) return;
    if (!travelerPendingRef.current || String(travelerPendingRef.current.saleNo) !== String(s)) return;
    const nextRect = getMouseAnchorRect(e);
    if (!nextRect) return;
    travelerPendingRef.current = { ...travelerPendingRef.current, anchorRect: nextRect };
  };

  const handleOrderRowMouseLeave = () => {
    if (travelerHoverTimerRef.current) {
      clearTimeout(travelerHoverTimerRef.current);
      travelerHoverTimerRef.current = null;
    }
    if (travelerCloseTimerRef.current) clearTimeout(travelerCloseTimerRef.current);
    travelerCloseTimerRef.current = setTimeout(() => {
      closeTravelerPopover();
    }, 120);
  };

  useEffect(() => {
    if (!travelerPopover.open) return;
    const onScroll = (ev) => {
      try {
        if (travelerPopoverRootRef.current && ev?.target && travelerPopoverRootRef.current.contains(ev.target)) return;
      } catch (_) {}
      closeTravelerPopover();
    };
    const onResize = () => closeTravelerPopover();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [travelerPopover.open]);

  // 일괄 상태 변경 관련 상태
  const [checkedSaleNos, setCheckedSaleNos] = useState(new Set());
  const [saleStatusCodes, setSaleStatusCodes] = useState([]);
  const [selectedStatusCode, setSelectedStatusCode] = useState('');
  const [isBatchUpdating, setIsBatchUpdating] = useState(false);

  // 마리트 엑셀 가져오기 처리 함수
  const handleMaritImport = async (reservationGroups, options = {}) => {
try {
      // 실제 API 호출로 미등록상품예약 생성
      const result = await createMaritReservationBatch(reservationGroups, {
        onProgress: typeof options.onProgress === 'function'
          ? options.onProgress
          : () => {}
      });
const succeeded = result?.summary?.succeeded || 0;
      const failed = result?.summary?.failed || 0;
      if (result.success) {
        // 모든 주문이 성공적으로 생성됨
        AntModal.info({
          title: '마리트 예약 생성 완료',
          content: `${succeeded}개 성공, ${failed}개 실패`,
        });
        
      } else {
        // 일부 또는 전체 실패
        console.warn(`⚠️ ${succeeded}개 성공, ${failed}개 실패`);
        
        // 실패한 주문 정보 출력
        result.errors.forEach((error, index) => {
          console.error(`실패 ${index + 1}: ${error.reservationDate} - ${error.error}`);
        });
        
        // 성공한 주문이 있다면 출력
        if (result.results.length > 0) {
result.results.forEach((order, index) => {
});
        }

        // 요약 안내 모달
        AntModal.warning({
          title: '일부 주문 생성 실패',
          content: `${succeeded}개 성공, ${failed}개 실패\n실패 항목은 재시도할 수 있습니다.`,
        });

        // 실패 그룹만 재시도 여부 확인 및 체크포인트 기반 재시도
        const failedGroups = (result.errors || []).map(e => reservationGroups[e.groupIndex]).filter(Boolean);
        const checkpointsByDate = {};
        (result.errors || []).forEach(e => {
          if (e?.checkpoint && e?.reservationDate) checkpointsByDate[e.reservationDate] = e.checkpoint;
        });

        if (failedGroups.length > 0) {
          AntModal.confirm({
            title: '실패한 주문 재시도',
            content: `${failedGroups.length}개 주문을 중단된 단계부터 재시도하시겠습니까?`,
            okText: '재시도',
            cancelText: '취소',
            onOk: async () => {
              try {
                const retryResult = await createMaritReservationBatch(failedGroups, {
                  checkpoints: checkpointsByDate,
                  onProgress: () => {}
                });
                const rSucceeded = retryResult?.summary?.succeeded || 0;
                const rFailed = retryResult?.summary?.failed || 0;
                AntModal.info({ title: '재시도 결과', content: `${rSucceeded}개 성공, ${rFailed}개 실패` });
                // 재시도 후에도 목록 새로고침
                await loadData(searchBasis, startDate, endDate);
              } catch (e) {
                message.error(e?.message || '재시도 중 오류가 발생했습니다.');
              }
            }
          });
        }
      }
      
      // 데이터 새로고침 (현재 화면의 검색 기준/날짜 범위 유지)
      await loadData(searchBasis, startDate, endDate);
      
      return result;
      
    } catch (error) {
      console.error('마리트 예약 생성 중 오류:', error);
      throw error;
    }
  };

  // 마리트 엑셀 가져오기 - 등록된 상품에 예약 추가
  const handleMaritImportToRegistered = async (reservationGroups, options = {}) => {
try {
      // 등록된 상품에 예약 추가
      const result = await createMaritReservationForRegisteredProducts(reservationGroups, {
        onProgress: typeof options.onProgress === 'function'
          ? options.onProgress
          : () => {}
      });
const succeeded = result?.summary?.succeeded || 0;
      const failed = result?.summary?.failed || 0;
      
      if (result.success) {
// 생성된 주문 정보 출력
        result.results.forEach((order, index) => {
});
        
        AntModal.success({
          title: '등록된 상품 예약 생성 완료',
          content: `${succeeded}개 성공, ${failed}개 실패`,
        });
        
      } else {
        console.warn(`⚠️ ${succeeded}개 성공, ${failed}개 실패`);
        
        // 실패한 주문 정보 출력
        result.errors.forEach((error, index) => {
          console.error(`실패 ${index + 1}: ${error.productName} - ${error.error}`);
        });
        
        // 성공한 주문이 있다면 출력
        if (result.results.length > 0) {
result.results.forEach((order, index) => {
});
        }
        
        AntModal.warning({
          title: '일부 주문 생성 실패',
          content: `${succeeded}개 성공, ${failed}개 실패\n실패한 항목은 상품코드 매칭 또는 가격 정보를 확인해주세요.`,
        });
      }
      
      // 데이터 새로고침 (현재 화면의 검색 기준/날짜 범위 유지)
      await loadData(searchBasis, startDate, endDate);
      
      return result;
      
    } catch (error) {
      console.error('등록된 상품 예약 생성 중 오류:', error);
      AntModal.error({
        title: '예약 생성 실패',
        content: error.message || '알 수 없는 오류가 발생했습니다.',
      });
      throw error;
    }
  };

  const getColType = (key) => columns.find((c) => c.key === key)?.type || 'string';
  const parseNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const num = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(num) ? num : null;
  };

  
  const parseDate = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const s = String(v).trim();
    let dateStr = s;
    if (s.includes(' ')) dateStr = s.split(' ')[0];
    if (dateStr.includes('T')) dateStr = dateStr.split('T')[0];
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };
  // Sorting state and helpers
  const [sortKey, setSortKey] = useState(null); // column key or null
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const toggleSort = (key) => {
    setSortKey((prevKey) => {
      if (prevKey !== key) {
        setSortDir('asc');
        return key;
      }
      setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
      return key;
    });
  };

  const [openHeaderFilterKey, setOpenHeaderFilterKey] = useState(null);
  const [headerFilterSearch, setHeaderFilterSearch] = useState({});
  const [headerFilterSelected, setHeaderFilterSelected] = useState({});
  const headerFilterRootRef = useRef(null);
  const headerFilterAnchorElRef = useRef(null);
  const [headerFilterAnchorRect, setHeaderFilterAnchorRect] = useState(null);

  // 컬럼 표시/숨김 상태 저장 (정산 임베디드=sessionStorage, 일반=localStorage)
  useEffect(() => {
    if (!VISIBLE_COLS_STORAGE_KEY || typeof window === 'undefined') return;
    try {
      const storage = isSettlementReservationEmbedded ? window.sessionStorage : window.localStorage;
      const orderedKeys = TOGGLEABLE_COLUMNS
        .map((c) => c.key)
        .filter((k) => visibleCols.has(k));
      storage.setItem(VISIBLE_COLS_STORAGE_KEY, JSON.stringify(orderedKeys));
    } catch (_) {}
  }, [visibleCols, isSettlementReservationEmbedded, VISIBLE_COLS_STORAGE_KEY]);

  useEffect(() => {
    if (!openHeaderFilterKey) return;
    const onMouseDown = (e) => {
      const pop = headerFilterRootRef.current;
      const anchor = headerFilterAnchorElRef.current;
      if (pop && pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      setOpenHeaderFilterKey(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openHeaderFilterKey]);

  useEffect(() => {
    if (!openHeaderFilterKey) return;
    const updateRect = () => {
      const el = headerFilterAnchorElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openHeaderFilterKey]);
  function compareRows(a, b) {
    if (!sortKey) return 0;
    const type = getColType(sortKey);
    const getOrderNoNum = (row) => {
      const raw = row?.주문번호;
      if (raw === null || raw === undefined || raw === '') return null;
      const digits = String(raw).replace(/[^0-9]/g, '');
      const n = Number(digits);
      return Number.isFinite(n) ? n : null;
    };
    const getCompositeValue = (row, key) => {
      if (key === '예약정보') {
        return [row?.입금상태, row?.예약상태, row?.주문번호, row?.대리점]
          .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
          .join(' ');
      }
      if (key === '일정/상품') {
        return [row?.예약일, row?.행사시작일, row?.판매상품명]
          .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
          .join(' ');
      }
      return row?.[key];
    };
    const av = getCompositeValue(a, sortKey);
    const bv = getCompositeValue(b, sortKey);
    let cmp = 0;
    if (type === 'number') {
      const na = Number(String(av ?? '').replace(/,/g, ''));
      const nb = Number(String(bv ?? '').replace(/,/g, ''));
      const aNull = Number.isNaN(na);
      const bNull = Number.isNaN(nb);
      if (aNull && bNull) cmp = 0; else if (aNull) cmp = -1; else if (bNull) cmp = 1; else cmp = na - nb;
    } else if (type === 'date') {
      const ta = new Date(String(av ?? '')).getTime();
      const tb = new Date(String(bv ?? '')).getTime();
      const aNull = Number.isNaN(ta);
      const bNull = Number.isNaN(tb);
      if (aNull && bNull) cmp = 0; else if (aNull) cmp = -1; else if (bNull) cmp = 1; else cmp = ta - tb;

      // Tie-breaker: for same date, show latest reservation first (bigger orderNo first)
      if (cmp === 0) {
        const oa = getOrderNoNum(a);
        const ob = getOrderNoNum(b);
        if (oa === null && ob === null) return 0;
        if (oa === null) return 1;
        if (ob === null) return -1;
        return ob - oa;
      }
    } else {
      const sa = (av ?? '').toString();
      const sb = (bv ?? '').toString();
      cmp = sa.localeCompare(sb, 'ko');
    }
    return sortDir === 'asc' ? cmp : -cmp;
  }
  const onFilterChange = (key, value) => setColFilters((prev) => ({ ...prev, [key]: value }));
  const visibleColumns = useMemo(() => {
    const filtered = columns.filter((c) => visibleCols.has(c.key) && !FORCE_HIDDEN_COLUMN_KEYS.has(c.key));
    const indexOf = (k) => {
      const idx = colOrder.indexOf(k);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return filtered.sort((a, b) => indexOf(a.key) - indexOf(b.key));
  }, [visibleCols, colOrder]);

  const getHeaderFilterDisplay = (row, key) => {
    if (!row) return '';
    if (key === '예약정보') {
      const raw = [row?.입금상태, row?.예약상태, row?.주문번호, row?.대리점]
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
        .join(' ');
      return String(formatCellValue(key, raw) ?? '').trim();
    }
    if (key === '주문정보') {
      const raw = [row?.주문번호, row?.대리점]
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
        .join(' ');
      return String(formatCellValue(key, raw) ?? '').trim();
    }
    if (key === '일정/상품') {
      const raw = [row?.예약일, row?.행사시작일, row?.판매상품명]
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
        .join(' ');
      return String(formatCellValue(key, raw) ?? '').trim();
    }
    return String(formatCellValue(key, row?.[key]) ?? '').trim();
  };

  const toggleHeaderFilter = (key, anchorEl = null) => {
    setOpenHeaderFilterKey((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        headerFilterAnchorElRef.current = anchorEl;
        if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
          const r = anchorEl.getBoundingClientRect();
          setHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
        } else {
          setHeaderFilterAnchorRect(null);
        }
      } else {
        headerFilterAnchorElRef.current = null;
        setHeaderFilterAnchorRect(null);
      }
      return next;
    });
  };

  const onHeaderFilterSearchChange = (key, value) => {
    setHeaderFilterSearch((prev) => ({ ...prev, [key]: value }));
  };

  const isAllSelectedForKey = (key) => {
    const opts = columnValueOptions?.[key] || [];
    const selected = headerFilterSelected?.[key] || [];
    return opts.length > 0 && selected.length === opts.length;
  };

  const toggleAllForKey = (key) => {
    const opts = columnValueOptions?.[key] || [];
    setHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const next = current.length === opts.length ? [] : opts;
      return { ...prev, [key]: next };
    });
  };

  const toggleOptionForKey = (key, option) => {
    setHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const exists = current.includes(option);
      const next = exists ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  const persistColOrder = (order) => {
    try {
      const storage = (typeof window !== 'undefined' && isSettlementReservationEmbedded)
        ? window.sessionStorage
        : window.localStorage;
      storage.setItem(COL_ORDER_STORAGE_KEY, JSON.stringify(order));
    } catch (_) {}
  };
  const moveColBefore = (sourceKey, targetKey) => {
    setColOrder(prev => {
      if (!sourceKey || !targetKey || sourceKey === targetKey) return prev;
      const next = prev.slice();
      const sIdx = next.indexOf(sourceKey);
      const tIdx = next.indexOf(targetKey);
      if (sIdx === -1 || tIdx === -1) return prev;
      next.splice(sIdx, 1);
      // 삽입 위치 계산 (같은 인덱스로 드롭 시 변화 없음)
      const insertAt = tIdx;
      next.splice(insertAt, 0, sourceKey);
      persistColOrder(next);
      return next;
    });
  };

  // Reservation status code to design mapping
  const statusDesign = {
    '049001': { text: '예약접수', bg: 'bg-blue-50', border: 'border-blue-200', textc: 'text-blue-700' },
    '049002': { text: '변경접수', bg: 'bg-indigo-50', border: 'border-indigo-200', textc: 'text-indigo-700' },
    '049011': { text: '예약확정', bg: 'bg-emerald-50', border: 'border-emerald-200', textc: 'text-emerald-700' },
    '049019': { text: '최종확정', bg: 'bg-green-50', border: 'border-green-200', textc: 'text-green-700' },
    '049021': { text: '취소접수', bg: 'bg-orange-50', border: 'border-orange-200', textc: 'text-orange-700' },
    '049022': { text: '취소확정', bg: 'bg-amber-50', border: 'border-amber-200', textc: 'text-amber-700' },
    '049029': { text: '취소완료', bg: 'bg-red-50', border: 'border-red-200', textc: 'text-red-700' },
    '049031': { text: '예약불가', bg: 'bg-gray-100', border: 'border-gray-300', textc: 'text-gray-700' },
  };

  const renderStatusTag = (code, fallbackText) => {
    const meta = code && statusDesign[code];
    const label = fallbackText || (meta ? meta.text : '');
    if (!meta) {
      // No known code: show plain text to avoid hiding information
      return <span className="whitespace-nowrap">{label}</span>;
    }
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${meta.bg} ${meta.border} ${meta.textc}`}>
        {label}
      </span>
    );
  };

  // Determine if a row represents a cancelled status to tint the whole row red-ish
  const isCancelledRow = (record) => {
    const code = record?.예약상태코드 || record?.상태코드 || record?.StatusCode || '';
    if (['049021', '049022', '049029'].includes(String(code))) return true;
    const text = (record?.예약상태 || record?.상태 || record?.StatusText || '').toString();
    return text.includes('취소접수') || text.includes('취소 확정') || text.includes('취소확정') || text.includes('취소 완료') || text.includes('취소완료');
  };

  const computeDefaultDates = (basis) => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    if (String(basis) === '1') {
      const oneMonthAgo = new Date(today);
      oneMonthAgo.setMonth(today.getMonth() - 1);
      return { s: toYmd(oneMonthAgo), e: toYmd(today) };
    }
    const oneMonthLater = new Date(today);
    oneMonthLater.setMonth(today.getMonth() + 1);
    return { s: toYmd(today), e: toYmd(oneMonthLater) };
  };

  const computePresetDates = (basis, preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const addDays = (d, days) => {
      const x = new Date(d);
      x.setDate(x.getDate() + Number(days || 0));
      return x;
    };

    const addMonthsClamped = (d, months) => {
      const x = new Date(d);
      const day = x.getDate();
      x.setDate(1);
      x.setMonth(x.getMonth() + Number(months || 0));
      const lastDay = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
      x.setDate(Math.min(day, lastDay));
      return x;
    };

    const p = String(preset || 'today');
    const isReservationBasis = String(basis) === '1';
    const anchor = today;

    let from = anchor;
    let to = anchor;

    if (p === 'week') {
      from = isReservationBasis ? addDays(anchor, -7) : anchor;
      to = isReservationBasis ? anchor : addDays(anchor, 7);
    } else if (p === 'month') {
      from = isReservationBasis ? addMonthsClamped(anchor, -1) : anchor;
      to = isReservationBasis ? anchor : addMonthsClamped(anchor, 1);
    } else if (p === 'months3') {
      from = isReservationBasis ? addMonthsClamped(anchor, -3) : anchor;
      to = isReservationBasis ? anchor : addMonthsClamped(anchor, 3);
    } else {
      // 'today'
      from = anchor;
      to = anchor;
    }

    return { s: toYmd(from), e: toYmd(to) };
  };

  const getActiveQuickPreset = () => {
    if (!startDate || !endDate) return null;
    const presets = ['today', 'week', 'month', 'months3'];
    for (const p of presets) {
      const { s, e } = computePresetDates(searchBasis, p);
      if (s === startDate && e === endDate) return p;
    }
    return null;
  };

  const computeThisMonthRange = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth();
    const pad = (n) => String(n).padStart(2, '0');
    const start = `${y}-${pad(m + 1)}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const end = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
    return { start, end };
  };

  const parseNumber0 = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const num = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(num) ? num : 0;
  };

  const loadGroupSettlement = async (sDate, eDate, basis) => {
    try {
      setOrderLoading(true);
      setOrderError(null);
      const list = await getGroupSettlementMain({
        startDate: sDate,
        endDate: eDate,
        searchBasis: basis || '행사일',
        manager: 'NULL',
      });
      setOrderSettleRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Failed to fetch group settlement main', e);
      setOrderError(e?.message || '주문(행사단체 결산) 데이터를 불러오지 못했습니다.');
    } finally {
      setOrderLoading(false);
    }
  };

  const loadData = async (basis, sDate, eDate) => {
    try {
      setLoading(true);
      setError(null);
      const isSettlementReservationEmbedded = embedded && fixedMode === 'reservations';
      const [list, orderStatusList] = await Promise.all([
        getReservationTravelerList({ searchBasis: basis, startDate: sDate, endDate: eDate }),
        isSettlementReservationEmbedded
          ? (async () => {
            try {
              return await getOrderStatusList({ startDate: sDate, endDate: eDate, searchBasis: basis });
            } catch (e) {
              console.error('Failed to fetch order status list for sale memo', e);
              return [];
            }
          })()
          : Promise.resolve([]),
      ]);

      const pickSaleMemoFromRow = (r) => {
        if (!r || typeof r !== 'object') return '';
        const candidates = [
          r?.판매비고,
          r?.판매비고내용,
          r?.판매비고사항,
          r?.판매메모,
          r?.판매메모1,
          r?.비고,
          r?.메모,
          r?.Memo,
        ];
        for (const v of candidates) {
          const s = String(v ?? '').trim();
          if (s) return s;
        }
        try {
          for (const k of Object.keys(r)) {
            if (!k) continue;
            if (!String(k).includes('비고') && !String(k).toLowerCase().includes('memo') && !String(k).includes('메모')) continue;
            const s = String(r[k] ?? '').trim();
            if (s) return s;
          }
        } catch (_) {}
        return '';
      };

      const saleMemoByOrderNo = new Map();
      const saleMemoBySaleNo = new Map();
      const cityByOrderNo = new Map();
      const managerByOrderNo = new Map();
      if (isSettlementReservationEmbedded && Array.isArray(orderStatusList)) {
        for (const r of orderStatusList) {
          const memo = pickSaleMemoFromRow(r);
          if (!memo) continue;

          const orderNo = r?.주문번호 != null ? String(r.주문번호).trim() : '';
          if (orderNo) saleMemoByOrderNo.set(orderNo, memo);

          const saleNo = r?.판매번호 ?? r?.주문판매번호 ?? null;
          const saleNoKey = saleNo != null && String(saleNo).trim() !== '' ? String(saleNo).trim() : '';
          if (saleNoKey) saleMemoBySaleNo.set(saleNoKey, memo);
        }

        for (const r of orderStatusList) {
          const orderNo = r?.주문번호 != null ? String(r.주문번호).trim() : '';
          if (!orderNo) continue;

          const city = r?.도시;
          const cityStr = city === null || city === undefined ? '' : String(city).trim();
          if (cityStr && !cityByOrderNo.has(orderNo)) cityByOrderNo.set(orderNo, cityStr);

          const manager = r?.상품담당;
          const managerStr = manager === null || manager === undefined ? '' : String(manager).trim();
          if (managerStr && !managerByOrderNo.has(orderNo)) managerByOrderNo.set(orderNo, managerStr);
        }
      }

      // 기본 정렬: 행사일 기준 검색 시 행사시작일 오름차순
      let nextRows = Array.isArray(list) ? list.slice() : [];
      if (saleMemoByOrderNo.size > 0 || saleMemoBySaleNo.size > 0) {
        nextRows = nextRows.map((r) => {
          const orderNo = r?.주문번호 != null ? String(r.주문번호).trim() : '';
          const saleNo = r?.판매번호 ?? r?.주문판매번호 ?? null;
          const saleNoKey = saleNo != null && String(saleNo).trim() !== '' ? String(saleNo).trim() : '';
          const memo = (
            (orderNo ? (saleMemoByOrderNo.get(orderNo) || '') : '') ||
            (saleNoKey ? (saleMemoBySaleNo.get(saleNoKey) || '') : '')
          );
          if (!memo) return r;
          return { ...r, _saleMemo: memo };
        });
      }

      if (cityByOrderNo.size > 0 || managerByOrderNo.size > 0) {
        nextRows = nextRows.map((r) => {
          const orderNo = r?.주문번호 != null ? String(r.주문번호).trim() : '';
          if (!orderNo) return r;
          const next = { ...r };

          if (!next.도시) {
            const city = cityByOrderNo.get(orderNo);
            if (city) next.도시 = city;
          }

          if (!next.수배담당자) {
            const manager = managerByOrderNo.get(orderNo);
            if (manager) next.수배담당자 = manager;
          }

          return next;
        });
      }
      if (String(basis) === '2') {
        nextRows.sort((a, b) => {
          const da = parseDate(a?.행사시작일);
          const db = parseDate(b?.행사시작일);
          if (da === null && db === null) return 0;
          if (da === null) return 1;
          if (db === null) return -1;
          return da.getTime() - db.getTime();
        });
      } else if (String(basis) === '1') {
        nextRows.sort((a, b) => {
          const da = parseDate(a?.예약일);
          const db = parseDate(b?.예약일);
          if (da === null && db === null) return 0;
          if (da === null) return 1;
          if (db === null) return -1;
          return db.getTime() - da.getTime();
        });
      }

      nextRows = nextRows.map((r) => {
        const raw = r?.택스및유류 ?? r?.택스및유류원화 ?? r?.taxFuel ?? r?.TaxFuel;
        const extra = (raw === null || raw === undefined || raw === '') ? '' : parseNumber0(raw);
        return { ...r, 추가판매: extra };
      });
      setRows(nextRows);
    } catch (e) {
      console.error('Failed to fetch reservation traveler list', e);
      setError(e?.message || '예약 리스트를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openSaleMemoModal = ({ saleNo, orderNo, currentMemo }) => {
    setSaleMemoModalSaleNo(saleNo ?? null);
    setSaleMemoModalOrderNo(orderNo ? String(orderNo) : '');
    setSaleMemoModalValue(String(currentMemo ?? ''));
    setSaleMemoModalOpen(true);
  };

  const handleSaveSaleMemo = async () => {
    const saleNo = saleMemoModalSaleNo;
    if (saleNo == null || saleNo === '' || Number.isNaN(Number(saleNo))) {
      message.error('판매번호가 없어 판매비고를 저장할 수 없습니다.');
      return;
    }
    const memo = String(saleMemoModalValue ?? '');
    try {
      setSaleMemoSaving(true);
      await saveSaleMemo({ saleNo: Number(saleNo), memo });
      message.success('판매비고가 저장되었습니다.');

      const orderNo = (saleMemoModalOrderNo ?? '').toString().trim();
      const saleNoKey = String(saleNo).trim();
      setRows((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.map((r) => {
          const rOrderNo = r?.주문번호 != null ? String(r.주문번호).trim() : '';
          const rSaleNo = r?.판매번호 ?? r?.주문판매번호 ?? null;
          const rSaleNoKey = rSaleNo != null && String(rSaleNo).trim() !== '' ? String(rSaleNo).trim() : '';
          const hit = (orderNo && rOrderNo === orderNo) || (saleNoKey && rSaleNoKey === saleNoKey);
          if (!hit) return r;
          return { ...r, _saleMemo: memo };
        });
      });

      setSaleMemoModalOpen(false);
    } catch (e) {
      console.error('Failed to save sale memo', e);
      message.error(e?.message || '판매비고 저장에 실패했습니다.');
    } finally {
      setSaleMemoSaving(false);
    }
  };

  const handleReservationSearch = () => {
    if (!startDate || !endDate) {
      message.warning('조회기간을 선택해 주세요.');
      return;
    }
    let s = startDate;
    let e = endDate;
    if (String(s) > String(e)) {
      s = endDate;
      e = startDate;
      setStartDate(s);
      setEndDate(e);
    }
    loadData(searchBasis, s, e);
  };

  useEffect(() => {
    const { s, e } = computeDefaultDates(searchBasis);
    setStartDate(s);
    setEndDate(e);
    loadData(searchBasis, s, e);
  }, []);

  // Normalize inverted ranges (start > end) — 검색은 트리거하지 않음
  useEffect(() => {
    if (!startDate || !endDate) return;
    if (String(startDate) > String(endDate)) {
      setStartDate(endDate);
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const { start, end } = computeThisMonthRange();
    setOrderStartDate(start);
    setOrderEndDate(end);
  }, []);

  useEffect(() => {
    if (activeMode !== 'orders') return;
    if (!orderStartDate || !orderEndDate) return;
    loadGroupSettlement(orderStartDate, orderEndDate, orderSearchBasis);
  }, [activeMode]);

  // If navigated with an orderNo, auto switch to 예약일 기준 and filter by that order
  useEffect(() => {
    const st = (location && location.state) ? location.state : null;
    if (!st) return;
    const incomingOrderNo = st.orderNo ? String(st.orderNo) : '';
    if (!incomingOrderNo) return;
    const basis = st.searchBasis || '1'; // default to 예약일 기준
    setSearchBasis(basis);
    const { s, e } = computeDefaultDates(basis);
    setStartDate(s);
    setEndDate(e);
    setColFilters((prev) => ({ ...prev, 주문번호: incomingOrderNo }));
    loadData(basis, s, e);
  }, [location?.state]);

  // 동적으로 테이블 높이를 화면 높이에 맞추기 위한 상태
  const [tableScrollY, setTableScrollY] = useState(560);

  useEffect(() => {
    const updateTableHeight = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      // 아래쪽 여백 약간 확보 (패딩/푸터 등 고려)
      const paddingBottom = 24;
      const available = viewportHeight - rect.top - paddingBottom;
      // 너무 작아지지 않도록 최소 높이 보장
      const minHeight = 300;
      const nextHeight = Math.max(available, minHeight);
      setTableScrollY(nextHeight);
    };

    updateTableHeight();
    window.addEventListener('resize', updateTableHeight);
    return () => window.removeEventListener('resize', updateTableHeight);
  }, []);

  // 판매상태 코드 목록 로드
  useEffect(() => {
    const loadStatusCodes = async () => {
      try {
        const codes = await getSaleStatusCodes();
        setSaleStatusCodes(codes || []);
      } catch (e) {
        console.error('판매상태 코드 로드 실패:', e);
      }
    };
    loadStatusCodes();
  }, []);

  // 일괄 상태 변경 핸들러
  const handleBatchStatusChange = async () => {
    if (checkedSaleNos.size === 0) {
      message.warning('변경할 예약을 선택해주세요.');
      return;
    }
    if (!selectedStatusCode) {
      message.warning('변경할 상태를 선택해주세요.');
      return;
    }

    const statusName = saleStatusCodes.find(s => s.코드 === selectedStatusCode)?.코드명 || selectedStatusCode;
    
    AntModal.confirm({
      title: '예약 상태 일괄 변경',
      content: `선택한 ${checkedSaleNos.size}건의 예약을 "${statusName}"(으)로 변경하시겠습니까?`,
      okText: '변경',
      cancelText: '취소',
      onOk: async () => {
        try {
          setIsBatchUpdating(true);
          const saleNoList = Array.from(checkedSaleNos);
          await updateReservationBatch({ saleNoList, statusCode: selectedStatusCode });
          message.success(`${saleNoList.length}건의 예약 상태가 변경되었습니다.`);
          setCheckedSaleNos(new Set());
          setSelectedStatusCode('');
          // 데이터 새로고침
          await loadData(searchBasis, startDate, endDate);
        } catch (e) {
          console.error('일괄 상태 변경 실패:', e);
          message.error(e?.message || '상태 변경에 실패했습니다.');
        } finally {
          setIsBatchUpdating(false);
        }
      }
    });
  };

  // 그룹 체크박스 토글 (판매번호 기준)
  const toggleGroupCheck = (saleNo, checked) => {
    setCheckedSaleNos(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(saleNo);
      } else {
        next.delete(saleNo);
      }
      return next;
    });
  };

  // 전체 선택/해제
  const toggleAllGroups = (checked) => {
    if (checked) {
      // 현재 표시된 모든 그룹의 판매번호 수집
      const allSaleNos = new Set();
      grouped.forEach(g => {
        g.items.forEach(item => {
          const saleNo = item?.판매번호 ?? item?.주문판매번호;
          if (saleNo != null) {
            allSaleNos.add(Number(saleNo));
          }
        });
      });
      setCheckedSaleNos(allSaleNos);
    } else {
      setCheckedSaleNos(new Set());
    }
  };

  const filtered = useMemo(() => {
    if (showCancelled) return rows;
    return rows.filter((row) => !isCancelledRow(row));
  }, [rows, showCancelled]);

  const uiFiltered = useMemo(() => {
    const activeKeys = Object.keys(colFilters).filter((k) => (colFilters[k] ?? '').toString().trim() !== '');
    const gq = (globalFilter ?? '').toString().trim().toLowerCase();
    if (activeKeys.length === 0 && !gq) return filtered;
    const lowered = Object.fromEntries(activeKeys.map((k) => [k, colFilters[k].toString().toLowerCase()]));
    const getFilterRaw = (row, key) => {
      if (key === '예약정보') {
        return [row?.입금상태, row?.예약상태, row?.주문번호, row?.대리점]
          .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
          .join(' ');
      }
      if (key === '일정/상품') {
        return [row?.예약일, row?.행사시작일, row?.판매상품명]
          .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
          .join(' ');
      }
      return row?.[key];
    };
    // In /settlements embedded 예약조회 화면, 공통헤더(예약정보, 일정/상품)에 보여지는 값도
    // 전체검색(globalFilter) 대상에 포함되도록 별도의 검색 키 집합을 구성한다.
    const isSettlementReservationEmbedded = embedded && fixedMode === 'reservations';
    const globalSearchKeys = isSettlementReservationEmbedded
      ? ['예약정보', '일정/상품']
      : visibleColumns.map((c) => c.key);
    return filtered.filter((row) => {
      const colPass = activeKeys.every((k) => {
        const raw = getFilterRaw(row, k);
        if (raw === null || raw === undefined) return false;
        const display = (formatCellValue(k, raw) ?? '').toString().toLowerCase();
        return display.includes(lowered[k]);
      });
      if (!colPass) return false;
      if (!gq) return true;
      return globalSearchKeys.some((key) => {
        const raw = getFilterRaw(row, key);
        if (raw === null || raw === undefined) return false;
        const display = (formatCellValue(key, raw) ?? '').toString().toLowerCase();
        return display.includes(gq);
      });
    });
  }, [filtered, colFilters, globalFilter, visibleColumns, embedded, fixedMode]);

  const columnValueOptions = useMemo(() => {
    const arr = Array.isArray(uiFiltered) ? uiFiltered : [];
    const map = {};
    visibleColumns.forEach((c) => {
      const set = new Set();
      arr.forEach((row) => {
        const v = String(getHeaderFilterDisplay(row, c.key) ?? '').trim();
        if (v !== '') set.add(v);
      });
      map[c.key] = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'ko'));
    });
    return map;
  }, [uiFiltered, visibleColumns]);

  const headerFiltered = useMemo(() => {
    const isSettlementReservationEmbedded = embedded && fixedMode === 'reservations';
    if (!isSettlementReservationEmbedded) return uiFiltered;

    const activeKeys = Object.keys(headerFilterSelected || {}).filter(
      (k) => Array.isArray(headerFilterSelected?.[k]) && headerFilterSelected[k].length > 0
    );
    if (activeKeys.length === 0) return uiFiltered;
    const selectedMap = Object.fromEntries(
      activeKeys.map((k) => [k, new Set((headerFilterSelected[k] || []).map((v) => String(v)))])
    );
    return (Array.isArray(uiFiltered) ? uiFiltered : []).filter((row) =>
      activeKeys.every((k) => {
        const display = String(getHeaderFilterDisplay(row, k) ?? '').trim();
        return selectedMap[k].has(display);
      })
    );
  }, [uiFiltered, embedded, fixedMode, headerFilterSelected]);

  const sortedUiFiltered = useMemo(() => {
    if (!sortKey) return headerFiltered;
    const arr = headerFiltered.slice();
    arr.sort(compareRows);
    return arr;
  }, [headerFiltered, sortKey, sortDir]);

  // Aggregate: total product amount (상품금액) for current filtered reservation rows
  const totalReservationAmount = useMemo(() => {
    try {
      return uiFiltered.reduce((sum, row) => sum + parseNumber0(row?.상품금액), 0);
    } catch {
      return 0;
    }
  }, [uiFiltered]);

  // Group by 주문번호 (for visual grouping only)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of sortedUiFiltered) {
      const key = r?.주문번호 != null ? String(r.주문번호) : '기타';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries()).map(([groupKey, items]) => ({ groupKey, items }));
  }, [sortedUiFiltered]);

  // Build AntD columns (all right-aligned, nowrap) and data
  const antdColumns = useMemo(() => {
    const widthOverrides = {
      '예약정보': 220,
      '일정/상품': 320,
      예약일: 110,
      행사시작일: 110,
      판매상품명: 240,
      수배담당자: 100,
      도시: 120,
      연령구분: 110,
      한글이름: 140,
      성별: 90,
      영문성: 120,
      영문이름: 120,
      생년월일: 130,
      여권번호: 160,
      여권만료일: 130,
      여행객비고: 260,
      상품금액: 120,
      추가판매: 120,
      입금상태: 110,
      예약상태: 110,
      주문정보: 160,
      주문번호: 120,
      대리점: 140,
    };
    const defaultByType = { number: 120, date: 130, string: 160 };
    
    const checkboxColumn = {
      title: (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={checkedSaleNos.size > 0 && grouped.length > 0 && (() => {
              const allSaleNos = new Set();
              grouped.forEach(g => {
                g.items.forEach(item => {
                  const saleNo = item?.판매번호 ?? item?.주문판매번호;
                  if (saleNo != null) allSaleNos.add(Number(saleNo));
                });
              });
              return allSaleNos.size > 0 && [...allSaleNos].every(sn => checkedSaleNos.has(sn));
            })()}
            indeterminate={checkedSaleNos.size > 0 && !(() => {
              const allSaleNos = new Set();
              grouped.forEach(g => {
                g.items.forEach(item => {
                  const saleNo = item?.판매번호 ?? item?.주문판매번호;
                  if (saleNo != null) allSaleNos.add(Number(saleNo));
                });
              });
              return allSaleNos.size > 0 && [...allSaleNos].every(sn => checkedSaleNos.has(sn));
            })()}
            onChange={(e) => toggleAllGroups(e.target.checked)}
          />
        </div>
      ),
      dataIndex: '_checkbox',
      key: '_checkbox',
      align: 'center',
      width: 50,
      fixed: 'left',
      onHeaderCell: () => ({ 
        style: { 
          padding: '8px 4px', 
          backgroundColor: 'rgb(var(--gray-50))', 
          textAlign: 'center',
          borderBottom: '1px solid rgb(var(--gray-200))'
        } 
      }),
      onCell: (record) => ({
        rowSpan: record?._isFirstInGroup ? (record?._groupRowSpan || 1) : 0,
        style: {
          padding: '8px 4px',
          textAlign: 'center',
          verticalAlign: 'middle',
        }
      }),
      render: (_, record) => {
        if (!record?._isFirstInGroup) return null;
        const saleNo = record?.판매번호 ?? record?.주문판매번호 ?? null;
        const numSaleNo = saleNo != null ? Number(saleNo) : null;
        if (numSaleNo == null || Number.isNaN(numSaleNo)) return null;
        return (
          <Checkbox
            checked={checkedSaleNos.has(numSaleNo)}
            onChange={(e) => toggleGroupCheck(numSaleNo, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    };

    const cols = [
      checkboxColumn,
      ...visibleColumns.map((c) => ({
        title: (
          <div className="flex flex-col items-stretch gap-1">
            <div className="flex items-center justify-center gap-2">
              <span className="whitespace-nowrap text-xs font-medium text-gray-700">{c.label}</span>
              <button
                type="button"
                className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500"
                title="정렬"
                onClick={(e) => { e.stopPropagation(); toggleSort(c.key); }}
              >
                {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
              </button>
              {(embedded && fixedMode === 'reservations') && (
                <button
                  type="button"
                  className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(headerFilterSelected?.[c.key]) && headerFilterSelected[c.key].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                  title="필터"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleHeaderFilter(c.key, e.currentTarget);
                  }}
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
                </button>
              )}
            </div>
            {!(embedded && fixedMode === 'reservations') && (
              <AntInput
                size="small"
                placeholder=""
                value={colFilters[c.key] ?? ''}
                onChange={(e) => onFilterChange(c.key, e.target.value)}
                allowClear
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="text-xs"
              />
            )}
          </div>
        ),
        dataIndex: c.key,
        key: c.key,
        align: (c.key === '예약정보' || c.key === '일정/상품') ? 'left' : 'center',
        width: widthOverrides[c.key] ?? defaultByType[c.type] ?? 140,
        ellipsis: (c.key === '여행객비고' || c.key === '예약정보' || c.key === '일정/상품') ? false : true,
        onHeaderCell: () => ({ 
          style: { 
            padding: '8px 12px', 
            backgroundColor: 'rgb(var(--gray-50))', 
            fontWeight: 500, 
            textAlign: 'center',
            borderBottom: '1px solid rgb(var(--gray-200))'
          },
          draggable: true,
          onDragStart: (e) => {
            try { e.dataTransfer.setData('text/plain', c.key); } catch {}
            setDraggingColKey(c.key);
          },
          onDragOver: (e) => { e.preventDefault(); },
          onDrop: (e) => {
            e.preventDefault();
            const source = (() => { try { return e.dataTransfer.getData('text/plain') || draggingColKey; } catch { return draggingColKey; } })();
            moveColBefore(source, c.key);
            setDraggingColKey(null);
          }
        }),
        onCell: (record) => {
          const rowSpan = MERGED_ORDER_COLUMN_KEYS.has(c.key)
            ? (record?._isFirstInGroup ? (record?._groupRowSpan || 1) : 0)
            : 1;
          const isContinuationRowFirstVisibleCell = !record?._isFirstInGroup && c.key === '연령구분';
          return {
            rowSpan,
            style: {
              padding: '8px 12px',
              fontSize: '13px',
              color: 'rgb(var(--gray-700))',
              whiteSpace: (c.key === '여행객비고' || c.key === '예약정보' || c.key === '일정/상품') ? 'normal' : 'nowrap',
              lineHeight: (c.key === '여행객비고' || c.key === '예약정보' || c.key === '일정/상품') ? '1.2' : undefined,
              maxHeight: c.key === '여행객비고' ? '2.4em' : undefined,
              verticalAlign: rowSpan > 1
                ? 'middle'
                : ((c.key === '예약정보' || c.key === '일정/상품') ? 'top' : undefined),
              // When merged left cells continue across rows, Antd treats this as first visible cell
              // and adds left border, which makes the divider look thicker.
              borderLeft: isContinuationRowFirstVisibleCell ? '0' : undefined,
            }
          };
        },
        render: (value, record) => {
          if (c.key === '예약정보') {
            const deposit = String(record?.입금상태 || '-');
            const depositClass = deposit.includes('완납')
              ? 'bg-green-100 text-green-800'
              : deposit.includes('부분')
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800';
            const statusCode = record?.예약상태코드 || record?.상태코드 || record?.StatusCode;
            const statusText = formatCellValue('예약상태', record?.예약상태);
            const orderNo = formatCellValue('주문번호', record?.주문번호);
            const agency = formatCellValue('대리점', record?.대리점);
            return (
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-1">
                  {renderStatusTag(statusCode, statusText)}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${depositClass}`}>{deposit}</span>
                </div>
                <div className="text-xs text-gray-700">
                  <span className="text-gray-500">주문</span>
                  <span className="mx-1 text-gray-300">|</span>
                  <span className="font-mono">{orderNo}</span>
                </div>
                <div className="text-xs text-gray-600 line-clamp-2">
                  <span className="text-gray-500">대리점</span>
                  <span className="mx-1 text-gray-300">|</span>
                  <span>{agency || '-'}</span>
                </div>
              </div>
            );
          }

          if (c.key === '주문정보') {
            const orderNo = formatCellValue('주문번호', record?.주문번호);
            const agency = formatCellValue('대리점', record?.대리점);
            return (
              <div className="space-y-1">
                <div className="text-xs text-gray-700">
                  <span className="text-gray-500">주문</span>
                  <span className="mx-1 text-gray-300">|</span>
                  <span className="font-mono">{orderNo || '-'}</span>
                </div>
                <div className="text-xs text-gray-600 line-clamp-2">
                  <span className="text-gray-500">대리점</span>
                  <span className="mx-1 text-gray-300">|</span>
                  <span>{agency || '-'}</span>
                </div>
              </div>
            );
          }

          if (c.key === '일정/상품') {
            const rsvDate = formatCellValue('예약일', record?.예약일);
            const startDate = formatCellValue('행사시작일', record?.행사시작일);
            const productName = formatCellValue('판매상품명', record?.판매상품명);
            return (
              <div className="space-y-1">
                <div className="text-xs text-gray-600">
                  <span className="text-gray-500">예약</span>
                  <span className="mx-1 text-gray-300">|</span>
                  <span className="font-mono">{rsvDate}</span>
                  <span className="mx-2 text-gray-200">/</span>
                  <span className="text-gray-500">출발</span>
                  <span className="mx-1 text-gray-300">|</span>
                  <span className="font-mono">{startDate}</span>
                </div>
                <div className="text-sm text-gray-900 line-clamp-2">{productName || '-'}</div>
              </div>
            );
          }

          if (c.key === '예약상태') {
            const code = record?.예약상태코드 || record?.상태코드 || record?.StatusCode;
            const text = formatCellValue(c.key, value);
            return renderStatusTag(code, text);
          }

          if (c.key === '주문번호') {
            const orderNo = formatCellValue('주문번호', value);
            return <span className="font-mono text-sm text-gray-800">{orderNo || '-'}</span>;
          }

          if (c.key === '대리점') {
            const agency = formatCellValue('대리점', value);
            return <span className="text-sm text-gray-700">{agency || '-'}</span>;
          }

          const formatted = formatCellValue(c.key, value);

          if (c.key === '한글이름') {
            const travelerType = (record?.여행객종류 ?? '').toString().trim();
            if (travelerType === '취소') {
              return (
                <span className="inline-flex items-center gap-1">
                  <span className="text-sm text-gray-900">{formatted}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200">
                    취소
                  </span>
                </span>
              );
            }
          }

          // 여행객비고 컬럼은 '/' 기준으로 줄바꿈 처리하되, '/' 자체는 화면에 유지
          if (c.key === '여행객비고') {
            const parts = formatted ? String(formatted).split('/') : [];
            if (parts.length === 0) {
              return null;
            }
            return (
              <span className="text-xs text-gray-700 leading-tight break-words">
                {parts.map((part, idx) => {
                  const isLast = idx === parts.length - 1;
                  return (
                    <React.Fragment key={idx}>
                      {part}
                      {!isLast && (
                        <>
                          {'/'}
                          <br />
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </span>
            );
          }

          if (c.key === '도시') {
            const city = formatted ? String(formatted) : '';
            const shortCity = city.length > 7 ? `${city.slice(0, 7)}...` : city;
            return (
              <span className="text-sm text-gray-700" title={city || ''}>
                {shortCity || '-'}
              </span>
            );
          }

          return (
            <span
              className={`${c.type === 'number' ? 'tabular-nums font-medium' : ''} ${c.type === 'date' ? 'text-xs text-gray-600' : ''}`}
            >
              {formatted}
            </span>
          );
        },
      })),
    ];
    return cols;
  }, [visibleColumns, colFilters, sortKey, sortDir, checkedSaleNos, grouped, embedded, fixedMode, headerFilterSelected]);

  const totalTableWidth = useMemo(() => antdColumns.reduce((sum, col) => sum + (col.width || 160), 0), [antdColumns]);

  // Flatten data with grouping metadata for visual styling
  const antdData = useMemo(() => {
    const flat = [];
    grouped.forEach((g, gi) => {
      const len = g.items.length;
      g.items.forEach((row, idx) => {
        flat.push({
          ...row,
          key: `${g.groupKey}-${idx}`,
          _groupKey: g.groupKey,
          _groupIndex: gi,
          _groupRowSpan: len,
          _isFirstInGroup: idx === 0,
          _isLastInGroup: idx === len - 1,
        });
      });
    });
    return flat;
  }, [grouped]);

  const headerFilterPopover = useMemo(() => {
    const isSettlementReservationEmbedded = embedded && fixedMode === 'reservations';
    if (!isSettlementReservationEmbedded) return null;
    if (!openHeaderFilterKey) return null;
    if (!headerFilterAnchorRect) return null;

    const key = openHeaderFilterKey;
    const widthPx = Math.max(240, Math.min(360, headerFilterAnchorRect.width || 0));
    const left = Math.max(8, headerFilterAnchorRect.left);
    const topBase = headerFilterAnchorRect.top + (headerFilterAnchorRect.height || 0) + 6;

    return (
      <div
        ref={headerFilterRootRef}
        style={{ position: 'fixed', top: topBase, left, width: widthPx }}
        className="bg-white border border-gray-200 rounded-md shadow-lg z-50"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b border-gray-100">
          <input
            type="text"
            placeholder="검색..."
            value={headerFilterSearch?.[key] ?? ''}
            onChange={(e) => onHeaderFilterSearchChange(key, e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
          />
        </div>
        <div className="max-h-56 overflow-auto p-2 text-xs text-gray-700">
          <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
            <input type="checkbox" checked={isAllSelectedForKey(key)} onChange={() => toggleAllForKey(key)} />
            <span>(모두 선택)</span>
          </label>
          <div className="my-1 border-t border-gray-100" />
          {(columnValueOptions?.[key] || [])
            .filter((v) => {
              const q = String(headerFilterSearch?.[key] ?? '').trim().toLowerCase();
              if (!q) return true;
              return String(v).toLowerCase().includes(q);
            })
            .slice(0, 300)
            .map((v) => (
              <label key={String(v)} className="flex items-center gap-2 py-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={(headerFilterSelected?.[key] || []).includes(v)}
                  onChange={() => toggleOptionForKey(key, v)}
                />
                <span className="truncate">{String(v)}</span>
              </label>
            ))}
        </div>
      </div>
    );
  }, [embedded, fixedMode, openHeaderFilterKey, headerFilterAnchorRect, headerFilterSearch, columnValueOptions, headerFilterSelected]);

  const filteredOrderSettleRows = useMemo(() => {
    let arr = Array.isArray(orderSettleRows) ? orderSettleRows : [];
    const gno = (orderGroupNoFilter || '').trim();
    if (gno) arr = arr.filter((r) => String(r?.행사단체번호 ?? '').includes(gno));
    const gname = (orderGroupNameFilter || '').trim().toLowerCase();
    if (gname) arr = arr.filter((r) => String(r?.행사단체명 ?? '').toLowerCase().includes(gname));
    const q = (orderKeyword || '').trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) => [r?.행사단체명, r?.수배담당자, r?.대리점].some((v) => String(v ?? '').toLowerCase().includes(q)));
    }
    if (orderReservationStatusFilter !== 'all') {
      arr = arr.filter((r) => {
        const st = String(r?.예약상태명 || '');
        if (orderReservationStatusFilter === '확정') return st.includes('확정');
        if (orderReservationStatusFilter === '취소') return st.includes('취소');
        return !st.includes('확정') && !st.includes('취소');
      });
    }
    return arr;
  }, [orderSettleRows, orderGroupNoFilter, orderGroupNameFilter, orderKeyword, orderReservationStatusFilter]);

  const groupedOrderSettleRows = useMemo(() => {
    const rows2 = Array.isArray(filteredOrderSettleRows) ? filteredOrderSettleRows : [];
    const groupSaleMap = new Map();
    const numericKeys = [
      '행사인원2', '원화환산액', '출금액', '미지급액', '수익합계', '최종수익', '판매현황NET', '판매현황입금액', '판매현황미수액',
    ];

    const parseDateTime = (v) => {
      if (!v) return null;
      const s = String(v).trim();
      if (!s) return null;
      const norm = s.includes('T') ? s : s.replace(' ', 'T');
      const d = new Date(norm);
      return isNaN(d.getTime()) ? null : d;
    };

    const pickStartStr = (r) => r?.시작일 ?? null;
    const pickEndStr = (r) => r?.종료일 ?? null;

    const getSaleNoKey = (r) => {
      const s = r?.판매번호 ?? r?.주문판매번호 ?? null;
      if (s == null || s === '') return `NO_SALE__${r?.주문번호 ?? Math.random()}`;
      return String(s);
    };
    for (const r of rows2) {
      const rawGroupKey = r?.행사단체번호;
      const groupKey = (rawGroupKey != null && String(rawGroupKey).length > 0)
        ? String(rawGroupKey)
        : `NO_GROUP__${getSaleNoKey(r)}`;
      const saleKey = getSaleNoKey(r);
      if (!groupSaleMap.has(groupKey)) groupSaleMap.set(groupKey, new Map());
      const saleMap = groupSaleMap.get(groupKey);
      const snap = { ...r };
      for (const k of numericKeys) snap[k] = parseNumber0(r?.[k]);
      saleMap.set(saleKey, snap);
    }
    const result = [];
    for (const [groupKey, saleMap] of groupSaleMap.entries()) {
      const sales = Array.from(saleMap.values());
      if (sales.length === 0) continue;
      const first = sales[0];
      const agg = { ...first };
      agg.대표판매번호 = first?.판매번호 ?? first?.주문판매번호 ?? null;
      agg.대표주문번호 = first?.주문번호 ?? null;
      agg.행사단체번호 = first?.행사단체번호 ?? (groupKey.startsWith('NO_GROUP__') ? '' : groupKey);
      if (!agg?.행사단체명) {
        const named = sales.find((x) => x?.행사단체명);
        if (named) agg.행사단체명 = named.행사단체명;
      }
      agg.판매건수 = Number(first?.판매건수) || 0;
      agg.취소건수 = Number(first?.취소건수) || 0;
      agg.행사인원2 = Number(first?.행사인원2 ?? first?.행사인원) || 0;
      agg.최종수익 = Number(first?.최종수익) || 0;

      if (!agg.대리점) {
        const aRow = sales.find((x) => String(x?.대리점 || '').trim());
        if (aRow) agg.대리점 = aRow.대리점;
      }

      // 행사 시작/종료일시 보강: 단체 내 최소 시작일 / 최대 종료일 사용
      let minStart = null;
      let minStartStr = '';
      let maxEnd = null;
      let maxEndStr = '';
      for (const sRow of sales) {
        const startStr = pickStartStr(sRow);
        const endStr = pickEndStr(sRow) || startStr;
        const startDt = parseDateTime(startStr);
        const endDt = parseDateTime(endStr);
        if (startDt && (!minStart || startDt < minStart)) {
          minStart = startDt;
          minStartStr = String(startStr || '');
        }
        if (endDt && (!maxEnd || endDt > maxEnd)) {
          maxEnd = endDt;
          maxEndStr = String(endStr || '');
        }
      }
      if (!agg.시작일 && minStartStr) agg.시작일 = minStartStr;
      if (!agg.종료일 && maxEndStr) agg.종료일 = maxEndStr;

      const sum = (key) => sales.reduce((acc, cur) => acc + (Number(cur?.[key]) || 0), 0);
      agg.원화환산액 = sum('원화환산액');
      agg.출금액 = sum('출금액');
      agg.미지급액 = sum('미지급액');
      agg.수익합계 = sum('수익합계');
      agg.판매현황NET = sum('판매현황NET');
      agg.판매현황입금액 = sum('판매현황입금액');
      agg.판매현황미수액 = sum('판매현황미수액');
      result.push(agg);
    }
    return result;
  }, [filteredOrderSettleRows]);

  const canShowTabsHeader = !hideTabs && !fixedMode;
  const [stickyOffset, setStickyOffset] = useState(0);

  useEffect(() => {
    if (!isSettlementReservationEmbedded) return;

    const updateStickyOffset = () => {
      const tabListEl = document.querySelector('[aria-label="정산 목록 전환"]');
      const stickyEl = tabListEl?.closest?.('.sticky') || tabListEl;
      const h = stickyEl?.getBoundingClientRect?.().height || 0;
      // Table header sticks below the settlement tabs only (search/sum scrolls away)
      setStickyOffset(Math.ceil(h));
    };

    updateStickyOffset();
    window.addEventListener('resize', updateStickyOffset);
    return () => window.removeEventListener('resize', updateStickyOffset);
  }, [isSettlementReservationEmbedded, activeMode]);
  const TabsHeader = canShowTabsHeader ? (
    <div className="inline-flex items-center bg-gray-50 p-1 rounded-xl" role="tablist" aria-label="예약/주문 조회 전환">
      <button
        type="button"
        role="tab"
        aria-selected={activeMode === 'reservations'}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
          activeMode === 'reservations'
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
        }`}
        onClick={() => setActiveMode('reservations')}
      >
        예약 조회
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={activeMode === 'orders'}
        className={`ml-1 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
          activeMode === 'orders'
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
        }`}
        onClick={() => setActiveMode('orders')}
      >
        주문 조회
      </button>
    </div>
  ) : null;

  const ReservationTableStyles = (
    <style>{`
        ${embedded ? '' : `
        main.overflow-auto {
          overflow: hidden !important;
        }
        `}
        ${isSettlementReservationEmbedded ? `
        .settlement-reservation-table .ant-table-tbody > tr > td {
          padding-top: 12px !important;
          padding-bottom: 12px !important;
          vertical-align: middle;
        }

        .settlement-reservation-table .ant-table-tbody > tr.ant-table-measure-row > td {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          border: 0 !important;
          height: 0 !important;
          line-height: 0 !important;
        }
        ` : ''}
        /* Visual grouping by 주문번호: alternate group shading + left color bar */
        .reservation-table .order-group-even td {
          background-color: rgb(var(--card)) !important;
        }
        .reservation-table .order-group-odd td {
          background-color: rgb(var(--gray-100)) !important;
        }
        .reservation-table .ant-table-tbody > tr.order-group-even:hover > td,
        .reservation-table .ant-table-tbody > tr.order-group-even.ant-table-row:hover > td {
          background-color: rgb(var(--card)) !important;
        }
        .reservation-table .ant-table-tbody > tr.order-group-odd:hover > td,
        .reservation-table .ant-table-tbody > tr.order-group-odd.ant-table-row:hover > td {
          background-color: rgb(var(--gray-100)) !important;
        }
        .reservation-table .order-group-c0 td:first-child { box-shadow: inset 5px 0 0 #2563eb; }
        .reservation-table .order-group-c1 td:first-child { box-shadow: inset 5px 0 0 #059669; }
        .reservation-table .order-group-c2 td:first-child { box-shadow: inset 5px 0 0 #d97706; }
        .reservation-table .order-group-c3 td:first-child { box-shadow: inset 5px 0 0 #db2777; }
        .reservation-table .order-group-c4 td:first-child { box-shadow: inset 5px 0 0 #7c3aed; }
        .reservation-table .order-group-c5 td:first-child { box-shadow: inset 5px 0 0 #0891b2; }
        /* 주문번호 그룹 하단 구분선 */
        .reservation-table:not(.settlement-reservation-table) .ant-table-tbody > tr.order-group-row-last > td {
          border-bottom: 3px solid rgb(var(--gray-500)) !important;
        }
        .reservation-table .ant-table-thead > tr > th {
          background-color: rgb(var(--gray-50)) !important;
          border-bottom: 1px solid rgb(var(--gray-200)) !important;
        }

      `}</style>
  );

  const orderTableColumns = useMemo(() => {
    return [
      {
        title: '행사단체명',
        key: '행사단체명',
        dataIndex: '행사단체명',
        width: 260,
        align: 'left',
        render: (_, row) => (
          <button
            type="button"
            className="text-sm font-medium text-black-600 hover:text-blue-800 hover:underline transition-colors flex items-start gap-1 max-xs"
            onClick={(e) => {
              e.stopPropagation();
              const s = row?.대표판매번호 ?? row?.판매번호 ?? null;
              if (!s) return;
              setGroupDrawerSaleNo(s);
              setGroupDrawerTitle(row?.행사단체명 || '단체정보');
              setGroupDrawerOpen(true);
            }}
            title="단체정보 보기"
          >
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="line-clamp-2 text-left">{row?.행사단체명 || '-'}</span>
          </button>
        ),
      },
      { title: '단체번호', dataIndex: '행사단체번호', key: '행사단체번호', width: 120, align: 'center', render: (v) => <span className="font-mono text-sm text-gray-600">{v ?? ''}</span> },
      { title: '대리점', dataIndex: '대리점', key: '대리점', width: 200, align: 'center', ellipsis: true, render: (v) => <span className="text-sm text-gray-700">{v || '-'}</span> },
      { title: '행사 시작일', dataIndex: '시작일', key: '시작일', width: 130, align: 'center', render: (v) => <span className="text-sm text-gray-600">{formatDateOnly(v)}</span> },
      { title: '행사 종료일', dataIndex: '종료일', key: '종료일', width: 130, align: 'center', render: (v) => <span className="text-sm text-gray-600">{formatDateOnly(v)}</span> },
      { title: '판매건수', dataIndex: '판매건수', key: '판매건수', width: 90, align: 'center' },
      { title: '취소건수', dataIndex: '취소건수', key: '취소건수', width: 90, align: 'center' },
      { title: '행사인원', dataIndex: '행사인원2', key: '행사인원2', width: 90, align: 'center' },
      { title: '판매가', dataIndex: '판매현황NET', key: '판매현황NET', width: 140, align: 'right', render: (v) => <span className="font-medium">{formatCurrency(v)}</span> },
      { title: '전도금(지출)', dataIndex: '원화환산액', key: '원화환산액', width: 140, align: 'right', render: (v) => <span className="font-medium">{formatCurrency(v)}</span> },
      { title: '최종수익', dataIndex: '최종수익', key: '최종수익', width: 140, align: 'center', render: (v) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          (v || 0) > 0 ? 'bg-green-50 text-green-700' : (v || 0) < 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'
        }`}>{formatCurrency(v)}</span>
      ) },
      { title: '예약상태', dataIndex: '예약상태명', key: '예약상태명', width: 120, align: 'center', render: (v) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          String(v || '').includes('확정') ? 'bg-green-50 text-green-700' : String(v || '').includes('취소') ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
        }`}>{v || '-'}</span>
      ) },
      { title: '정산 마감', dataIndex: '마감상태명', key: '마감상태명', width: 110, align: 'center', render: (v) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          v === '마감' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-700'
        }`}>{v || '-'}</span>
      ) },
    ];
  }, []);

  const orderTotalTableWidth = useMemo(
    () => orderTableColumns.reduce((sum, c) => sum + (c.width || 160), 0),
    [orderTableColumns]
  );

  const rootClassName = embedded
    ? 'flex flex-col'
    : '-m-3 -mx-13 p-4 h-[calc(100vh-64px)] flex flex-col overflow-hidden';

  if (activeMode === 'orders') {
    const travelerPopoverNode = travelerPopover.open && travelerPopover.anchorRect ? (() => {
      const rect = travelerPopover.anchorRect;
      const popoverWidth = 1080;
      const popoverMaxHeight = 360;
      const margin = 8;
      const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
      const preferredLeft = rect.right + 10;
      const left = Math.max(margin, Math.min(preferredLeft + popoverWidth > viewportW ? rect.left - 10 - popoverWidth : preferredLeft, viewportW - margin - popoverWidth));
      const top = Math.max(margin, Math.min(rect.top, viewportH - margin - popoverMaxHeight));
      return createPortal(
        <div
          ref={travelerPopoverRootRef}
          style={{ position: 'fixed', top, left, width: popoverWidth, maxHeight: popoverMaxHeight, zIndex: 1200 }}
          className="bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden"
          onMouseEnter={() => {
            if (travelerCloseTimerRef.current) {
              clearTimeout(travelerCloseTimerRef.current);
              travelerCloseTimerRef.current = null;
            }
          }}
          onMouseLeave={() => closeTravelerPopover()}
        >
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-800">여행객 명단</div>
            <div className="text-xs text-gray-500">{Array.isArray(travelerPopover.list) ? travelerPopover.list.length : 0}명</div>
          </div>
          <div className="p-3 overflow-auto" style={{ maxHeight: popoverMaxHeight - 44 }}>
            {travelerPopover.loading && (
              <div className="text-sm text-gray-600">불러오는 중입니다...</div>
            )}
            {!travelerPopover.loading && travelerPopover.error && (
              <div className="text-sm text-red-600">{travelerPopover.error}</div>
            )}
            {!travelerPopover.loading && !travelerPopover.error && (!travelerPopover.list || travelerPopover.list.length === 0) && (
              <div className="text-sm text-gray-500">표시할 여행객이 없습니다.</div>
            )}
            {!travelerPopover.loading && !travelerPopover.error && Array.isArray(travelerPopover.list) && travelerPopover.list.length > 0 && (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="text-left font-medium pb-2 pr-2">한글이름</th>
                    <th className="text-center font-medium pb-2 pr-2">성별</th>
                    <th className="text-left font-medium pb-2 pr-2">영문성</th>
                    <th className="text-left font-medium pb-2 pr-2">영문이름</th>
                    <th className="text-center font-medium pb-2 pr-2">생년월일</th>
                    <th className="text-left font-medium pb-2 pr-2">여권번호</th>
                    <th className="text-center font-medium pb-2 pr-2">여권만료일</th>
                    <th className="text-left font-medium pb-2">여행객비고</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {travelerPopover.list.map((t, idx) => (
                    <tr key={String(t?.여행객번호 ?? idx)} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{t?.한글이름 || '-'}</td>
                      <td className="py-1.5 pr-2 text-center whitespace-nowrap">{t?.성별 || '-'}</td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{t?.영문성 || '-'}</td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{t?.영문이름 || '-'}</td>
                      <td className="py-1.5 pr-2 text-center whitespace-nowrap">{formatCellValue('생년월일', t?.생년월일)}</td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{t?.여권번호 || '-'}</td>
                      <td className="py-1.5 pr-2 text-center whitespace-nowrap">{formatCellValue('여권만료일', t?.여권만료일)}</td>
                      <td className="py-1.5 whitespace-pre-wrap break-words">{t?.여행객비고 || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>,
        document.body
      );
    })() : null;

    return (
      <div className={rootClassName}>
        {ReservationTableStyles}
        <div className="space-y-2 pb-2">
          <div className="flex items-center justify-between">
            {TabsHeader}
            <div className="text-sm text-gray-600">결과: {groupedOrderSettleRows.length.toLocaleString('ko-KR')}건</div>
          </div>

          <div className="bg-white rounded-2xl p-5 flex flex-wrap items-end gap-3 items-center">
            <div className="flex items-center gap-2 h-9">
              <span className="text-xs text-gray-600 whitespace-nowrap">조회구분</span>
              <span className="text-gray-300">|</span>
              <select
                className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={orderSearchBasis}
                onChange={(e) => setOrderSearchBasis(e.target.value)}
              >
                <option value="행사일">행사일 기준</option>
                <option value="예약일">예약일 기준</option>
              </select>
            </div>
            <div className="flex items-center gap-2 h-9">
              <span className="text-xs text-gray-600 whitespace-nowrap">조회기간</span>
              <span className="text-gray-300">|</span>
              <div className="w-auto">
                <DateRangePicker
                  startDate={orderStartDate}
                  endDate={orderEndDate}
                  onStartDateChange={setOrderStartDate}
                  onEndDateChange={setOrderEndDate}
                  hideLabels
                  inline
                  disabled={orderLoading}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 h-9">
              <span className="text-xs text-gray-600 whitespace-nowrap">예약상태</span>
              <span className="text-gray-300">|</span>
              <select
                className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={orderReservationStatusFilter}
                onChange={(e) => setOrderReservationStatusFilter(e.target.value)}
              >
                <option value="all">전체</option>
                <option value="확정">확정</option>
                <option value="취소">취소</option>
                <option value="기타">기타</option>
              </select>
            </div>
            <div className="flex items-center gap-2 h-9">
              <span className="text-xs text-gray-600 whitespace-nowrap">행사단체번호</span>
              <span className="text-gray-300">|</span>
              <input type="text" className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" value={orderGroupNoFilter} onChange={(e) => setOrderGroupNoFilter(e.target.value)} />
            </div>
            <div className="flex items-center gap-2 h-9">
              <span className="text-xs text-gray-600 whitespace-nowrap">행사단체명</span>
              <span className="text-gray-300">|</span>
              <input type="text" className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" value={orderGroupNameFilter} onChange={(e) => setOrderGroupNameFilter(e.target.value)} />
            </div>
            <div className="flex items-center gap-2 h-9">
              <span className="text-xs text-gray-600 whitespace-nowrap">검색</span>
              <span className="text-gray-300">|</span>
              <input type="text" className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" value={orderKeyword} onChange={(e) => setOrderKeyword(e.target.value)} placeholder="단체명/수배담당/대리점" />
            </div>
            <button
              type="button"
              className="inline-flex items-center h-9 px-3.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              onClick={() => loadGroupSettlement(orderStartDate, orderEndDate, orderSearchBasis)}
              disabled={orderLoading}
            >
              조회
            </button>
          </div>
        </div>

        {!orderLoading && orderError && (
          <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{orderError}</div>
        )}

        <div ref={containerRef} className={`mt-4 flex-1 min-h-0 bg-white rounded-2xl ${isSettlementReservationEmbedded ? 'overflow-visible' : 'overflow-hidden'} reservation-table ${embedded && fixedMode === 'reservations' ? 'settlement-reservation-table' : ''}`}> 
          <Table
            size={density === 'compact' ? 'small' : 'middle'}
            pagination={false}
            tableLayout="fixed"
            bordered
            loading={orderLoading}
            scroll={isSettlementReservationEmbedded ? { x: orderTotalTableWidth } : { x: orderTotalTableWidth, y: tableScrollY }}
            sticky={{ offsetHeader: isSettlementReservationEmbedded ? stickyOffset : 0 }}
            rowKey={(row, idx) => String(row?.행사단체번호 ?? row?.대표판매번호 ?? idx)}
            rowClassName={(_, index) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
            onRow={(row) => ({
              onClick: () => {
                const s = row?.대표판매번호 ?? row?.판매번호 ?? null;
                if (!s) return;
                setGroupDrawerSaleNo(s);
                setGroupDrawerTitle(row?.행사단체명 || '단체정보');
                setGroupDrawerOpen(true);
              },
              onMouseEnter: (e) => handleOrderRowMouseEnter(e, row),
              onMouseMove: (e) => handleOrderRowMouseMove(e, row),
              onMouseLeave: () => handleOrderRowMouseLeave(),
              style: { cursor: 'pointer' },
            })}
            columns={orderTableColumns}
            dataSource={groupedOrderSettleRows}
          />
          {!orderLoading && !orderError && groupedOrderSettleRows.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500">표시할 데이터가 없습니다. 조건을 변경해 보세요.</div>
          )}
        </div>

        <GroupInfoDrawer
          open={groupDrawerOpen}
          onClose={() => setGroupDrawerOpen(false)}
          saleNo={groupDrawerSaleNo}
          title={groupDrawerTitle}
        />
        {travelerPopoverNode}
      </div>
    );
  }


  return (
    <div className={embedded ? '' : 'p-6'}>
      <AntModal
        open={saleMemoModalOpen}
        title={(
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">판매비고 {saleMemoModalOrderNo ? `(주문번호: ${saleMemoModalOrderNo})` : ''}</span>
          </div>
        )}
        onCancel={() => {
          if (!saleMemoSaving) setSaleMemoModalOpen(false);
        }}
        okText="저장"
        cancelText="취소"
        onOk={handleSaveSaleMemo}
        confirmLoading={saleMemoSaving}
        maskClosable={!saleMemoSaving}
        keyboard={!saleMemoSaving}
        destroyOnClose
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-500">비고사항을 입력해 주세요.</div>
          <AntInput.TextArea
            value={saleMemoModalValue}
            onChange={(e) => setSaleMemoModalValue(e.target.value)}
            rows={6}
            placeholder="판매비고"
            maxLength={4000}
            showCount
          />
        </div>
      </AntModal>
      {ReservationTableStyles}

      {/* Top toolbar + 검색 영역 (스크롤과 함께 이동; 테이블 헤더만 sticky) */}
      <div>
        <div className="flex items-center justify-between">
          {TabsHeader}
          <div className="text-xs text-gray-600 flex items-center gap-2">
            <span className="whitespace-nowrap">상품금액 합계</span>
            <span className="font-mono tabular-nums">{formatCurrency(totalReservationAmount)}</span>
          </div>
        </div>

        {/* 검색 영역 */}
        <div data-tour="reservation-search" className="bg-white rounded-2xl p-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-gray-600 whitespace-nowrap">조회구분</span>
                <select
                  className="px-2.5 h-9 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={searchBasis}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchBasis(val);
                    const { s, e: ee } = computeDefaultDates(val);
                    setStartDate(s);
                    setEndDate(ee);
                  }}
                >
                  <option value="1">예약일 기준</option>
                  <option value="2">행사일 기준</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-gray-600 whitespace-nowrap">조회기간</span>
                <div className="flex items-center gap-2">
                  <DateRangePicker
                      startDate={startDate}
                      endDate={endDate}
                      onStartDateChange={setStartDate}
                      onEndDateChange={setEndDate}
                      hideLabels
                      inline
                      disabled={loading}
                    />
                  {(startDate || endDate) && (
                    <button
                      type="button"
                      onClick={() => { setStartDate(''); setEndDate(''); }}
                      className="h-9 px-3 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      title="기간 초기화"
                    >지우기</button>
                  )}
                  <button
                    type="button"
                    className="inline-flex items-center h-9 px-3.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                    onClick={handleReservationSearch}
                    disabled={loading || !startDate || !endDate}
                  >
                    조회
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 ml-2">
                <span className="text-[11px] text-gray-500">빠른 선택</span>
                <button
                  type="button"
                  className={`h-9 px-3 rounded-lg border text-sm font-semibold ${
                    getActiveQuickPreset() === 'today'
                      ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    const { s, e } = computePresetDates(searchBasis, 'today');
                    setStartDate(s);
                    setEndDate(e);
                    loadData(searchBasis, s, e);
                  }}
                  title="오늘"
                >오늘</button>
                <button
                  type="button"
                  className={`h-9 px-3 rounded-lg border text-sm font-semibold ${
                    getActiveQuickPreset() === 'week'
                      ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    const { s, e } = computePresetDates(searchBasis, 'week');
                    setStartDate(s);
                    setEndDate(e);
                    loadData(searchBasis, s, e);
                  }}
                  title="일주일"
                >일주일</button>
                <button
                  type="button"
                  className={`h-9 px-3 rounded-lg border text-sm font-semibold ${
                    getActiveQuickPreset() === 'month'
                      ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    const { s, e } = computePresetDates(searchBasis, 'month');
                    setStartDate(s);
                    setEndDate(e);
                    loadData(searchBasis, s, e);
                  }}
                  title="한달"
                >한달</button>
                <button
                  type="button"
                  className={`h-9 px-3 rounded-lg border text-sm font-semibold ${
                    getActiveQuickPreset() === 'months3'
                      ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    const { s, e } = computePresetDates(searchBasis, 'months3');
                    setStartDate(s);
                    setEndDate(e);
                    loadData(searchBasis, s, e);
                  }}
                  title="3개월"
                >3개월</button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="show-cancelled"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                />
                <label htmlFor="show-cancelled" className="text-xs text-gray-600 select-none">
                  취소예약 보기
                </label>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 relative">
              <button
                data-tour="reservation-new"
                type="button"
                className="inline-flex items-center h-9 px-3.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
                onClick={() => setNewModalOpen(true)}
              >
                <svg className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                신규예약 추가
              </button>
              <button type="button" className="inline-flex items-center h-9 px-3.5 text-sm font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50" onClick={() => setShowColMenu(v => !v)}>
                열 표시/숨김
              </button>
              <button
                data-tour="reservation-export"
                type="button"
                className="inline-flex items-center h-9 px-3.5 text-sm font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => {
                  try {
                    const headers = visibleColumns.map(c => c.label);
                    const keys = visibleColumns.map(c => c.key);
                    const getExportRaw = (row, key) => {
                      if (key === '예약정보') {
                        return [row?.입금상태, row?.예약상태, row?.주문번호, row?.대리점]
                          .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
                          .join(' ');
                      }
                      if (key === '주문정보') {
                        return [row?.주문번호, row?.대리점]
                          .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
                          .join(' ');
                      }
                      if (key === '일정/상품') {
                        return [row?.예약일, row?.행사시작일, row?.판매상품명]
                          .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
                          .join(' ');
                      }
                      return row?.[key];
                    };
                    const csvContent = '\ufeff' + [
                      headers.join(','),
                      ...sortedUiFiltered.map(row => keys.map(key => `"${String(getExportRaw(row, key) || '').replace(/"/g, '""')}"`).join(','))
                    ].join('\r\n');
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    if (link.download !== undefined) {
                      const url = URL.createObjectURL(blob);
                      link.setAttribute('href', url);
                      link.setAttribute('download', `reservations_${new Date().toISOString().slice(0, 10)}.csv`);
                      link.style.visibility = 'hidden';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  } catch (e) {
                    console.error('Export error:', e);
                  }
                }}
              >
                <svg className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                엑셀 다운로드
              </button>
              {showColMenu && (
                <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded shadow p-2 w-56 max-h-64 overflow-auto">
                  <div className="text-[11px] text-gray-500 mb-1">표시할 열 선택</div>
                  {TOGGLEABLE_COLUMNS.map(c => (
                    <label key={c.key} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleCols.has(c.key)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setVisibleCols((prev) => applyVisibleColToggle(prev, c.key, checked));
                        }}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                  <div className="pt-2 mt-1 border-t border-gray-100 flex justify-between">
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => {
                        // 개별 열 기준으로 모두 표시 (합쳐진 열과 중복되지 않도록)
                        const next = new Set(
                          TOGGLEABLE_COLUMNS
                            .map((c) => c.key)
                            .filter((k) => !COMPOSITE_TO_PARTS[k])
                        );
                        setVisibleCols(next);
                      }}
                    >
                      모두 선택
                    </button>
                    <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setShowColMenu(false)}>닫기</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 일괄 상태 변경 영역 */}
        {checkedSaleNos.size > 0 && (
          <div data-tour="reservation-batch" className="bg-blue-50 p-3 rounded border border-blue-200 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-blue-700">
              {checkedSaleNos.size}건 선택됨
            </span>
            <div className="flex items-center gap-2">
              <select
                className="px-2 py-1.5 border border-blue-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedStatusCode}
                onChange={(e) => setSelectedStatusCode(e.target.value)}
              >
                <option value="">상태 선택</option>
                {saleStatusCodes.map((s) => (
                  <option key={s.코드} value={s.코드}>{s.코드명}</option>
                ))}
              </select>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleBatchStatusChange}
                disabled={!selectedStatusCode || isBatchUpdating}
              >
                {isBatchUpdating ? '변경 중...' : '일괄 변경'}
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                onClick={() => {
                  setCheckedSaleNos(new Set());
                  setSelectedStatusCode('');
                }}
              >
                선택 해제
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toolbar removed: 검색 입력은 위 검색 영역에 배치됨 */}

      {false && visibleColumns.length > 0 && (
        <div className="mb-2 mt-2 flex flex-wrap items-center gap-2">
          {visibleColumns.map((c) => (
            <div key={c.key} className="flex items-center gap-1">
              <span className="text-[11px] text-gray-600">{c.label}</span>
              <AntInput
                size="small"
                placeholder="필터"
                style={{ width: 140 }}
                value={colFilters[c.key] ?? ''}
                onChange={(e) => onFilterChange(c.key, e.target.value)}
                allowClear
              />
            </div>
          ))}
        </div>
      )}

      {/* Loading / Error / Empty states */}
      {!loading && error && (
        <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
      )}

      <div data-tour="reservation-table" ref={containerRef} className={`mt-4 flex-1 min-h-0 bg-white rounded-2xl ${isSettlementReservationEmbedded ? 'overflow-visible' : 'overflow-hidden'} reservation-table ${embedded && fixedMode === 'reservations' ? 'settlement-reservation-table' : ''}`}>
          {headerFilterPopover}
          <Table
            key={`reservation-cols-${visibleColumns.map((c) => c.key).join('|')}`}
            size={density === 'compact' ? 'small' : 'middle'}
            pagination={false}
            tableLayout="fixed"
            bordered
            loading={loading}
            scroll={isSettlementReservationEmbedded ? { x: totalTableWidth } : { x: totalTableWidth, y: tableScrollY }}
            sticky={{ offsetHeader: isSettlementReservationEmbedded ? stickyOffset : 0 }}
          rowKey={(record) => record.key || `${record.주문번호 || ''}-${record.행사시작일 || ''}`}
          rowClassName={(record) => {
            const canceled = isCancelledRow(record);
            const gi = Number(record?._groupIndex) || 0;
            const groupParityClass = gi % 2 === 0 ? 'order-group-even' : 'order-group-odd';
            const groupColorClass = `order-group-c${Math.abs(gi) % 6}`;
            return [
              groupParityClass,
              groupColorClass,
              record._isLastInGroup ? 'order-group-row-last' : '',
              canceled ? 'text-red-600 line-through' : '',
            ].filter(Boolean).join(' ');
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                const orderNo = record?.주문번호;
                if (orderNo) {
                  const saleNo = record?.판매번호 ?? record?.주문판매번호 ?? null;
                  const productName = record?.판매상품명 ?? null;
                  try { console.debug('[ReservationManagement] navigate to detail', { orderNo, saleNo, productName, record }); } catch {}
                  navigate(`/reservation-detail/${orderNo}` , { state: { saleNo, productName } });
                }
              },
              style: {
                cursor: 'pointer',
              },
            };
          }}
            columns={antdColumns}
            dataSource={antdData}
          />
        {!loading && !error && antdData.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">표시할 데이터가 없습니다. 조건을 변경해 보세요.</div>
        )}
      </div>
      {/* 신규예약 모달 */}
      <NewReservationModal open={newModalOpen} onClose={() => setNewModalOpen(false)} />
      
      {/* 마리트 엑셀 가져오기 모달 */}
      {canUseMaritImport && (
        <MaritReservationImportModal 
          open={maritImportModalOpen} 
          onClose={() => setMaritImportModalOpen(false)}
          onApply={handleMaritImport}
          onApplyToRegistered={handleMaritImportToRegistered}
        />
      )}
    </div>
  );
};

export default ReservationManagement;
